import React from 'react';
import { FaTimes } from 'react-icons/fa';
import '../styles/InventoryEquipment.css';
import '../styles/InventoryAudit.css';

function formatDate(raw) {
  if (!raw || raw === 'Pending') return raw || '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function typeLabel(type) {
  const map = {
    ExcessUsage: 'Excess Usage',
    MissingEquipment: 'Missing Equipment',
    MissingInventory: 'Missing Inventory',
    TheftRisk: 'Theft Risk',
    LowProductivity: 'Low Productivity',
  };
  return map[type] || type || '—';
}

function typeClass(type) {
  const map = {
    ExcessUsage: 'iaud-chip--yellow',
    MissingEquipment: 'iaud-chip--blue',
    MissingInventory: 'iaud-chip--blue',
    TheftRisk: 'iaud-chip--red',
    LowProductivity: 'iaud-chip--blue',
  };
  return map[type] || 'iaud-chip--gray';
}

function severityClass(s) {
  if (s === 'Critical') return 'iaud-chip--red';
  if (s === 'High') return 'iaud-chip--orange';
  return 'iaud-chip--yellow';
}

function statusClass(s) {
  if (s === 'Open') return 'iaud-chip--red';
  if (s === 'Investigating') return 'iaud-chip--yellow';
  if (s === 'Resolved') return 'iaud-chip--green';
  return 'iaud-chip--gray';
}

function formatVariance(n) {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v}%`;
}

function varianceTone(n) {
  if (n == null || n === '') return '';
  const v = Math.abs(Number(n));
  if (v >= 80) return 'iaud-var--critical';
  if (v >= 40) return 'iaud-var--high';
  if (v >= 20) return 'iaud-var--medium';
  return '';
}

function dotClass(kind) {
  if (kind === 'generated') return 'iaud-dot--red';
  if (kind === 'discrepancy' || kind === 'investigating') return 'iaud-dot--orange';
  if (kind === 'resolved') return 'iaud-dot--green';
  return 'iaud-dot--gray';
}

export default function AuditAlertModal({
  alert,
  onClose,
  onDismiss,
  onEscalate,
  onResolve,
  saving,
}) {
  if (!alert) return null;
  const activity = Array.isArray(alert.activity) ? alert.activity : [];
  const statusKey = String(alert.status || '');
  const canResolve =
    statusKey === 'Open' || statusKey === 'Investigating';
  const canEscalate = statusKey !== 'Investigating';
  const canDismiss = statusKey !== 'Dismissed';

  return (
    <div className="ieq-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="ieq-modal ieq-modal--detail iaud-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ieq-modal-header">
          <h2>Audit Alert — {alert.alertNumber}</h2>
          <button
            type="button"
            className="ieq-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="ieq-modal-body">
          <div className="iaud-chips">
            <span className="iaud-chip iaud-chip--gray">{alert.alertNumber}</span>
            <span className={`iaud-chip ${severityClass(alert.severity)}`}>
              {alert.severity}
            </span>
            <span className={`iaud-chip ${statusClass(alert.status)}`}>
              {alert.status}
            </span>
            <span className={`iaud-chip ${typeClass(alert.alertType)}`}>
              {typeLabel(alert.alertType)}
            </span>
          </div>

          <div className="ieq-detail-cards">
            <div className="ieq-detail-card">
              <b>Date Detected</b>
              <span>{formatDate(alert.detectedAt)}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Site</b>
              <span>{alert.site || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Product / Asset</b>
              <span>{alert.productAsset || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Alert Type</b>
              <span>{typeLabel(alert.alertType)}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Expected</b>
              <span>{alert.expectedValue || '—'}</span>
            </div>
            <div className="ieq-detail-card">
              <b>Actual</b>
              <span>{alert.actualValue || '—'}</span>
            </div>
          </div>

          <div className={`iaud-variance ${varianceTone(alert.variancePercent)}`}>
            <span>Variance</span>
            <strong>{formatVariance(alert.variancePercent)}</strong>
          </div>

          <section className="iaud-timeline">
            <h4>Activity Timeline</h4>
            <ul>
              {activity.map((ev, i) => (
                <li key={`${ev.at}-${i}`}>
                  <i className={`iaud-dot ${dotClass(ev.kind)}`} />
                  <div>
                    <b>{formatDate(ev.at)}</b>{' '}
                    <span>{ev.message}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="ieq-modal-footer iaud-modal-footer">
          <button
            type="button"
            className="ieq-btn"
            disabled={saving || !canDismiss}
            onClick={() => onDismiss?.(alert)}
          >
            Dismiss
          </button>
          <button
            type="button"
            className="ieq-btn"
            disabled={saving || !canEscalate}
            onClick={() => onEscalate?.(alert)}
          >
            Escalate
          </button>
          {canResolve && (
            <button
              type="button"
              className="ieq-btn iaud-btn-resolve"
              disabled={saving}
              onClick={() => onResolve?.(alert)}
            >
              Mark as Resolved
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
